import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Check, ListChecks, ArrowRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import SEO from "../components/SEO";
import InlineLeadForm from "../components/InlineLeadForm";
import { getInsight, insights } from "../data/insights";
import { services } from "../data/services";

const SITE = "https://kandkassociates.com";

const Paragraph = ({ text, dropcap }) => (
  <p className={dropcap ? "dropcap" : ""}>{text}</p>
);

export default function InsightDetail() {
  const { slug } = useParams();
  const post = getInsight(slug);
  const relatedPosts = useMemo(
    () => (post ? insights.filter((p) => p.slug !== post.slug && p.category === post.category).concat(
      insights.filter((p) => p.slug !== post.slug && p.category !== post.category)
    ).slice(0, 3) : []),
    [post]
  );
  if (!post) return <Navigate to="/insights" replace />;

  const isLong = Array.isArray(post.sections) && post.sections.length > 0;
  const relatedServices = (post.relatedServices || [])
    .map((s) => services.find((x) => x.slug === s))
    .filter(Boolean);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: post.title,
        description: post.metaDescription || post.excerpt,
        datePublished: post.date,
        dateModified: post.updated || post.date,
        author: { "@type": "Organization", name: "K & K Associates" },
        publisher: {
          "@type": "Organization",
          name: "K & K Associates",
          url: SITE,
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/insights/${post.slug}` },
        keywords: (post.keywords || []).join(", "),
        articleSection: post.category,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE}/insights` },
          { "@type": "ListItem", position: 3, name: post.title },
        ],
      },
      ...(post.faqs?.length
        ? [{
            "@type": "FAQPage",
            mainEntity: post.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }]
        : []),
    ],
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.metaDescription || post.excerpt}
        canonical={`${SITE}/insights/${post.slug}`}
        type="article"
        keywords={post.keywords}
        schema={schema}
      />
      <section className="pt-28 md:pt-36 pb-14 md:pb-20 bg-paper">
        <div className="max-w-[860px] mx-auto px-6 lg:px-0">
          <nav className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink/50 mb-10">
            <Link to="/" className="hover:text-brand">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/insights" className="hover:text-brand">Insights</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-brand truncate max-w-[200px]">{post.category}</span>
          </nav>

          <p className="kicker mb-5">{post.category}</p>
          <h1 className="font-display text-4xl md:text-5xl text-ink leading-[1.05]">{post.title}</h1>
          {post.excerpt && <p className="mt-6 font-serif text-xl text-ink/70 leading-relaxed">{post.excerpt}</p>}
          <p className="mt-6 font-sans text-sm text-ink/50 tracking-wider">
            {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
            {post.updated && post.updated !== post.date && (
              <> · Updated {new Date(post.updated).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</>
            )}
            {" · "}{post.readTime} · By K &amp; K Associates
          </p>

          <div className="fine-rule w-24 my-12" />

          {isLong ? (
            <LongFormBody post={post} />
          ) : (
            <article className="font-serif text-lg md:text-xl text-ink/85 leading-[1.85] space-y-7">
              {post.body.map((p, i) => (
                <Paragraph key={p.slice(0, 40)} text={p} dropcap={i === 0} />
              ))}
              <InlineLeadForm source={`Blog: ${post.title}`} variant="dark"
                heading="Have a question about your situation?"
                subline="Leave your name and number — a partner will call you back within one business day." />
            </article>
          )}

          {/* Related services */}
          {relatedServices.length > 0 && (
            <div className="mt-16 border border-rule p-8 bg-bone" data-testid="blog-related-services">
              <p className="kicker mb-5">How K &amp; K Associates Can Help</p>
              <div className="flex flex-wrap gap-3">
                {relatedServices.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="inline-flex items-center gap-2 border border-rule bg-paper px-4 py-2.5 font-serif text-base text-ink hover:text-brand hover:border-brand transition-colors">
                    {s.title.split("—")[0].trim()} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-16 border-t border-rule pt-10 flex items-center justify-between">
            <Link to="/insights" className="inline-flex items-center gap-2 text-brand text-xs tracking-[0.25em] uppercase link-underline">
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Insights
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 text-ink text-xs tracking-[0.25em] uppercase link-underline hover:text-brand">
              Book a Consultation <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-bone border-t border-rule py-16">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-8">Continue Reading</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedPosts.map((p) => (
              <Link
                key={p.slug}
                to={`/insights/${p.slug}`}
                className="group p-8 bg-paper border border-rule hover:border-brand transition-colors"
                data-testid={`related-post-${p.slug}`}
              >
                <p className="kicker mb-3">{p.category}</p>
                <h3 className="font-display text-xl text-ink leading-tight group-hover:text-brand">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

const LongFormBody = ({ post }) => {
  const midpoint = Math.ceil(post.sections.length / 2);
  return (
    <div data-testid="blog-longform">
      {/* Table of contents */}
      <nav className="mb-12 border border-rule bg-bone p-6 md:p-8" data-testid="blog-toc">
        <div className="flex items-center gap-3 mb-4">
          <ListChecks className="w-5 h-5 text-brand" strokeWidth={1.5} />
          <p className="kicker">In This Guide</p>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 font-serif text-base text-ink/80 list-decimal list-inside">
          {post.sections.map((sec, i) => (
            <li key={sec.heading}><a href={`#g-${i}`} className="hover:text-brand link-underline">{sec.heading}</a></li>
          ))}
        </ol>
      </nav>

      {/* Key takeaways */}
      {post.keyTakeaways?.length > 0 && (
        <div className="mb-12 border-l-4 border-brand bg-bone p-6 md:p-8" data-testid="blog-takeaways">
          <p className="kicker mb-4">Key Takeaways</p>
          <ul className="space-y-3 font-serif text-lg text-ink/85">
            {post.keyTakeaways.map((t) => (
              <li key={t.slice(0, 40)} className="flex gap-3"><Check className="w-5 h-5 text-brand mt-1.5 shrink-0" strokeWidth={1.5} /><span>{t}</span></li>
            ))}
          </ul>
        </div>
      )}

      <article className="font-serif text-lg md:text-xl text-ink/85 leading-[1.85] space-y-7">
        {post.intro?.map((p, i) => <Paragraph key={p.slice(0, 40)} text={p} dropcap={i === 0} />)}

        {post.sections.map((sec, i) => (
          <React.Fragment key={sec.heading}>
            <div id={`g-${i}`} className="pt-6 scroll-mt-28">
              <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-6 mt-4">{sec.heading}</h2>
              {sec.paras?.map((p) => <p key={p.slice(0, 40)} className="mt-5">{p}</p>)}
              {sec.bullets?.length > 0 && (
                <ul className="space-y-3 mt-6">
                  {sec.bullets.map((b) => (
                    <li key={(typeof b === "string" ? b : b.t).slice(0, 40)} className="flex gap-3 text-lg leading-relaxed">
                      <Check className="w-5 h-5 text-brand mt-1.5 shrink-0" strokeWidth={1.5} />
                      <span>{typeof b === "string" ? b : <><strong className="text-ink">{b.t}:</strong> {b.d}</>}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {i === midpoint - 1 && (
              <InlineLeadForm source={`Blog: ${post.title}`} variant="dark"
                heading="Need advice specific to your case?"
                subline="Every situation is different. Leave your name and number — a K & K Associates partner will call you back within one business day." />
            )}
          </React.Fragment>
        ))}
      </article>

      {/* FAQ */}
      {post.faqs?.length > 0 && (
        <div className="mt-16" data-testid="blog-faqs">
          <p className="kicker mb-4">Frequently Asked Questions</p>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-8">{post.title.split(":")[0]} — FAQs</h2>
          <Accordion type="single" collapsible className="border-t border-rule">
            {post.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-rule">
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-ink hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-serif text-lg text-ink/75 leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {/* Disclaimer */}
      <p className="mt-12 text-sm font-sans text-ink/50 leading-relaxed border border-rule bg-bone p-5">
        <strong className="text-ink/70">Disclaimer:</strong> This article is for general information only and does not constitute legal advice. Laws change and every matter turns on its own facts. For advice on your specific situation, consult a qualified advocate. K &amp; K Associates offers a free first consultation.
      </p>
    </div>
  );
};
